import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, Modal, Badge, Spinner, ListGroup } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import ProjectMemberPicker from "../../components/ProjectMemberPicker";
import { usePermissions } from "../../context/PermissionContext";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";

const STATUS_VALUES = ["PLANNED", "ACTIVE", "ON_HOLD", "COMPLETED", "ARCHIVED"];

const initialForm = {
  name: "",
  code: "",
  description: "",
  owner_user_id: "",
  display_order: "",
};

// Phase 1B. Reusable module-management block, embedded within
// ProjectDetailPage's "Project Modules" tab — modules are always accessed
// within their project's context, never as a standalone top-level page.
// `projectMembers` must be the project's ACTIVE members (already fetched
// by the parent) — used both as the module-owner picker's eligible set
// ("module owner should also be a project member") and as the
// module-member picker's eligible set ("module member should normally be
// an active project member").
export default function ProjectModuleManagement({ projectId, projectMembers = [], onChanged }) {
  const { can } = usePermissions();

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [memberModuleId, setMemberModuleId] = useState(null);
  const [moduleMembers, setModuleMembers] = useState([]);
  const [loadingModuleMembers, setLoadingModuleMembers] = useState(false);
  const [newModuleMemberUserId, setNewModuleMemberUserId] = useState("");
  const [deleteModuleTarget, setDeleteModuleTarget] = useState(null);
  const [deletingModule, setDeletingModule] = useState(false);
  const [removeMemberTarget, setRemoveMemberTarget] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);

  const eligibleOwnerUsers = useMemo(
    () => projectMembers.map((pm) => ({
      id: pm.user_id,
      name: pm.user_name,
      employee_code: pm.employee_code,
      designation_name: pm.designation_name,
      department_name: pm.department_name,
    })),
    [projectMembers]
  );

  useEffect(() => {
    if (projectId) fetchModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchModules = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/projects/${projectId}/modules`, { params: { pageSize: 200 } });
      setModules(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Fetch modules error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch project modules");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const openAdd = () => {
    setForm(initialForm);
    setEditId(null);
    setShowModal(true);
  };

  const openEdit = (module_) => {
    setForm({
      name: module_.name || "",
      code: module_.code || "",
      description: module_.description || "",
      owner_user_id: module_.owner_user_id || "",
      display_order: module_.display_order ?? "",
    });
    setEditId(module_.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(initialForm);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warning("Module name is required");
      return;
    }

    const payload = {
      name: form.name.trim(),
      code: form.code.trim() || undefined,
      description: form.description.trim() || null,
      owner_user_id: form.owner_user_id || null,
      display_order: form.display_order === "" ? 0 : Number(form.display_order),
    };

    try {
      setSaving(true);
      if (editId) {
        await api.put(`/projects/${projectId}/modules/${editId}`, payload);
        toast.success("Module updated");
      } else {
        await api.post(`/projects/${projectId}/modules`, payload);
        toast.success("Module created");
      }
      closeModal();
      fetchModules();
      onChanged && onChanged();
    } catch (err) {
      console.error("Save module error:", err);
      toast.error(err.response?.data?.message || "Failed to save module");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (module_) => {
    try {
      await api.patch(`/projects/${projectId}/modules/${module_.id}/active-status`, { is_active: !module_.is_active });
      toast.success(module_.is_active ? "Module deactivated" : "Module activated");
      fetchModules();
      onChanged && onChanged();
    } catch (err) {
      console.error("Toggle module active error:", err);
      toast.error(err.response?.data?.message || "Failed to update module status");
    }
  };

  const changeStatus = async (module_, status) => {
    try {
      await api.patch(`/projects/${projectId}/modules/${module_.id}/status`, { status });
      toast.success(`Module status set to ${status}`);
      fetchModules();
    } catch (err) {
      console.error("Change module status error:", err);
      toast.error(err.response?.data?.message || "Failed to change module status");
    }
  };

  const confirmDeleteModule = async () => {
    if (!deleteModuleTarget) return;
    setDeletingModule(true);
    try {
      await api.delete(`/projects/${projectId}/modules/${deleteModuleTarget.id}`);
      toast.success("Module deleted");
      setDeleteModuleTarget(null);
      fetchModules();
      onChanged && onChanged();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "MODULE_IN_USE") {
        toast.error(`Cannot delete: ${data.details?.members || 0} module member(s) reference it. Deactivate instead.`);
      } else {
        toast.error(data?.message || "Failed to delete module");
      }
    } finally {
      setDeletingModule(false);
    }
  };

  // --- Module members modal --------------------------------------------------

  const openMemberModal = async (module_) => {
    setMemberModuleId(module_.id);
    setNewModuleMemberUserId("");
    await fetchModuleMembers(module_.id);
  };

  const closeMemberModal = () => {
    setMemberModuleId(null);
    setModuleMembers([]);
  };

  const fetchModuleMembers = async (moduleId) => {
    try {
      setLoadingModuleMembers(true);
      const res = await api.get(`/projects/${projectId}/modules/${moduleId}/members`, { params: { pageSize: 200 } });
      setModuleMembers(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error("Fetch module members error:", err);
      toast.error(err.response?.data?.message || "Failed to fetch module members");
    } finally {
      setLoadingModuleMembers(false);
    }
  };

  const addModuleMember = async () => {
    if (!newModuleMemberUserId) return;
    try {
      await api.post(`/projects/${projectId}/modules/${memberModuleId}/members`, { user_id: newModuleMemberUserId });
      toast.success("Module member added");
      setNewModuleMemberUserId("");
      fetchModuleMembers(memberModuleId);
      fetchModules();
    } catch (err) {
      console.error("Add module member error:", err);
      toast.error(err.response?.data?.message || "Failed to add module member");
    }
  };

  const confirmRemoveModuleMember = async () => {
    if (!removeMemberTarget) return;
    setRemovingMember(true);
    try {
      await api.delete(`/projects/${projectId}/modules/${memberModuleId}/members/${removeMemberTarget.id}`);
      toast.success("Module member removed");
      setRemoveMemberTarget(null);
      fetchModuleMembers(memberModuleId);
      fetchModules();
    } catch (err) {
      console.error("Remove module member error:", err);
      toast.error(err.response?.data?.message || "Failed to remove module member");
    } finally {
      setRemovingMember(false);
    }
  };

  const activeModuleMemberUserIds = moduleMembers.filter((m) => m.is_active).map((m) => m.user_id);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Project Modules</h5>
          <p className="text-muted mb-0">Module owners must be active project members.</p>
        </div>
        <Can moduleKey="project_modules" actionKey="create">
          <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
            Add Module
          </Button>
        </Can>
      </div>

      {loading ? (
        <div className="unit-loader">
          <Spinner animation="border" />
          <p>Loading modules...</p>
        </div>
      ) : (
        <Table responsive className="unit-table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Name</th>
              <th>Code</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Members</th>
              <th>Active</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {modules.length > 0 ? (
              modules.map((module_) => (
                <tr key={module_.id}>
                  <td>{module_.display_order}</td>
                  <td><strong>{module_.name}</strong></td>
                  <td>{module_.code}</td>
                  <td>{module_.owner_name || "—"}</td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={module_.status}
                      style={{ minWidth: 120 }}
                      onChange={(e) => changeStatus(module_, e.target.value)}
                      disabled={!can("project_modules", "edit")}
                    >
                      {STATUS_VALUES.map((s) => (
                        <option key={s} value={s}>{s.replace("_", " ")}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Button variant="link" className="p-0" onClick={() => openMemberModal(module_)}>
                      {module_.active_member_count ?? 0} member(s)
                    </Button>
                  </td>
                  <td>
                    <Badge className={module_.is_active ? "unit-status" : "unit-status unit-status-inactive"}>
                      {module_.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td>
                    <div className="unit-actions">
                      <Can moduleKey="project_modules" actionKey="edit">
                        <Button
                          className="unit-icon-btn unit-edit"
                          title="Edit"
                          aria-label={`Edit ${module_.name || "module"}`}
                          onClick={() => openEdit(module_)}
                        >
                          <i className="fa-solid fa-pen" aria-hidden="true"></i>
                        </Button>
                      </Can>
                      {(can("project_modules", "activate") || can("project_modules", "deactivate")) && (
                        <Button
                          className="unit-icon-btn projdetail-toggle"
                          title={module_.is_active ? "Deactivate" : "Activate"}
                          aria-label={module_.is_active ? `Deactivate ${module_.name || "module"}` : `Activate ${module_.name || "module"}`}
                          onClick={() => toggleActive(module_)}
                        >
                          <i className={`fa-solid ${module_.is_active ? "fa-pause" : "fa-play"}`} aria-hidden="true"></i>
                        </Button>
                      )}
                      <Can moduleKey="project_modules" actionKey="delete">
                        <Button
                          className="unit-icon-btn unit-delete"
                          title="Delete"
                          aria-label={`Delete ${module_.name || "module"}`}
                          onClick={() => setDeleteModuleTarget(module_)}
                        >
                          <i className="fa-solid fa-trash" aria-hidden="true"></i>
                        </Button>
                      </Can>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8">
                  <div className="unit-empty">
                    <div className="unit-empty-icon">
                      <i className="fa-solid fa-layer-group" aria-hidden="true"></i>
                    </div>
                    <h5>No modules configured for this project.</h5>
                    <p>Add the first module for this project.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      {/* Add/Edit module modal */}
      <Modal
        show={showModal}
        onHide={closeModal}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Module" : "Add New Module"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label>Name</Form.Label>
              <Form.Control value={form.name} onChange={(e) => handleChange("name", e.target.value)} placeholder="e.g. Payments" />
            </div>
            <div className="col-md-6">
              <Form.Label>Code (optional)</Form.Label>
              <Form.Control value={form.code} onChange={(e) => handleChange("code", e.target.value)} placeholder="e.g. PAYMENTS" />
            </div>
            <div className="col-md-12">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => handleChange("description", e.target.value)} />
            </div>
            <div className="col-md-8">
              <Form.Label>Owner (must be an active project member)</Form.Label>
              <ProjectMemberPicker
                users={eligibleOwnerUsers}
                value={form.owner_user_id}
                onChange={(id) => handleChange("owner_user_id", id)}
                placeholder="Select module owner..."
              />
              {eligibleOwnerUsers.length === 0 && (
                <Form.Text className="text-danger">Add project members first before assigning a module owner.</Form.Text>
              )}
            </div>
            <div className="col-md-4">
              <Form.Label>Display Order</Form.Label>
              <Form.Control type="number" value={form.display_order} onChange={(e) => handleChange("display_order", e.target.value)} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeModal}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : editId ? "Update Module" : "Create Module"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Module members modal */}
      <Modal
        show={memberModuleId != null}
        onHide={closeMemberModal}
        centered
        className="unit-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Module Members</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Can moduleKey="project_module_members" actionKey="add">
            <div className="d-flex gap-2 mb-3">
              <div style={{ flex: 1 }}>
                <ProjectMemberPicker
                  users={eligibleOwnerUsers}
                  excludeUserIds={activeModuleMemberUserIds}
                  value={newModuleMemberUserId}
                  onChange={setNewModuleMemberUserId}
                  placeholder="Add an active project member..."
                />
              </div>
              <Button className="unit-btn unit-btn-primary" onClick={addModuleMember} disabled={!newModuleMemberUserId}>
                <i className="fa-solid fa-user-plus" aria-hidden="true"></i>
                Add
              </Button>
            </div>
          </Can>

          {loadingModuleMembers ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <ListGroup>
              {moduleMembers.filter((m) => m.is_active).map((m) => (
                <ListGroup.Item key={m.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>{m.user_name}</strong>{" "}
                    <span className="text-muted">
                      {[m.employee_code ? `#${m.employee_code}` : null, m.designation_name, m.department_name].filter(Boolean).join(" — ")}
                    </span>
                    <div><Badge bg="light" text="dark">{m.membership_type}</Badge></div>
                  </div>
                  <Can moduleKey="project_module_members" actionKey="remove">
                    <Button
                      className="unit-icon-btn unit-delete"
                      title="Remove"
                      aria-label={`Remove ${m.user_name || "member"}`}
                      onClick={() => setRemoveMemberTarget(m)}
                    >
                      <i className="fa-solid fa-trash" aria-hidden="true"></i>
                    </Button>
                  </Can>
                </ListGroup.Item>
              ))}
              {moduleMembers.filter((m) => m.is_active).length === 0 && (
                <ListGroup.Item className="text-muted">No members assigned to this module.</ListGroup.Item>
              )}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeMemberModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!deleteModuleTarget}
        onHide={() => setDeleteModuleTarget(null)}
        onConfirm={confirmDeleteModule}
        busy={deletingModule}
        title="Delete Module?"
        message={`Delete module "${deleteModuleTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
      />

      <ConfirmModal
        show={!!removeMemberTarget}
        onHide={() => setRemoveMemberTarget(null)}
        onConfirm={confirmRemoveModuleMember}
        busy={removingMember}
        title="Remove Module Member?"
        message={`Remove ${removeMemberTarget?.user_name || "this member"} from the module?`}
        confirmLabel="Remove"
        dialogClassName="admin-teal-modal admin-teal-modal-sm admin-teal-modal-danger"
      />
    </div>
  );
}
